import React, { useState, type FormEvent } from "react";
import { Button } from "../../components/button.tsx";
import { ErrorBanner } from "../../components/error-banner.tsx";
import { FieldGroup } from "../../components/field-group.tsx";
import { Input } from "../../components/input.tsx";
import { isTestRedirectUri } from "../../lib/test-redirect.ts";

interface RedirectUrisFormProps {
  uris: string[];
  onSave: (uris: string[]) => Promise<void>;
}

export function RedirectUrisForm({ uris, onSave }: RedirectUrisFormProps) {
  const [newUri, setNewUri] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = newUri.trim();
    if (!trimmed) {
      return;
    }
    setError(null);
    try {
      await onSave(Array.from(new Set([...uris, trimmed])));
      setNewUri("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleRemove(uri: string) {
    setError(null);
    try {
      await onSave(uris.filter((entry) => entry !== uri));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <ErrorBanner message={error} className="mb-4" />
      <form onSubmit={handleAdd}>
        <FieldGroup>
          <Input
            value={newUri}
            onChange={(event) => setNewUri(event.target.value)}
            placeholder="https://your-app.example.com/oauth/callback"
            required
          />
          <Button type="submit">Add</Button>
        </FieldGroup>
      </form>

      {uris.length > 0 ? (
        <ul className="mt-4 divide-y-2 divide-faint border-y-2 border-faint">
          {uris.map((uri) => (
            <li
              key={uri}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3"
            >
              <span
                className="min-w-0 flex-1 truncate font-mono text-xs text-ink"
                title={uri}
              >
                {uri}
              </span>
              {isTestRedirectUri(uri) ? (
                <span className="font-mono text-xs uppercase tracking-wider text-muted">
                  test
                </span>
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => handleRemove(uri)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

